import { FC, useEffect, useMemo, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { Switch } from '@headlessui/react';
import {
  guestUserListForSellerHookUrl,
  useGuestUserListForSellerHook,
} from '@shared/api/user/user.hook';
import { guestUserListForSellerStateSelector } from '@shared/api/user/user.selector';
import { ErrorFallback } from '@shared/components/ErrorFallback';
import { useAuth } from '@shared/state/hooks/useAuth';
import { PaymentState } from '@shared/types';
import { ErrorBoundary } from 'react-error-boundary';
import { useQueryClient } from 'react-query';

import { GuestCard } from '../components/GuestCard';

export const SellerGusetOrderManagePage: FC = function SellerGusetOrderManagePage() {
  const [showPaidGuests, setShowPaidGuests] = useState(false);

  useGuestUserListForSellerHook();
  const { sellerRole } = useAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    const interval = setInterval(() => {
      queryClient.invalidateQueries(guestUserListForSellerHookUrl(sellerRole()));
    }, 1000 * 60 * 10);
    return () => clearInterval(interval);
  }, [queryClient, sellerRole]);

  const guestUserListForSellerState = useRecoilValue(guestUserListForSellerStateSelector);

  const guestUserList = guestUserListForSellerState.data;

  const guestUserListRender = useMemo(() => {
    return guestUserList
      ?.slice()
      .sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA; // 나중에 생성된 것이 먼저 오도록 내림차순 정렬
      })
      .filter((guest) => {
        const shopOrderList = guest.storeCustomerDetail?.shopOrders;
        const isPaidGuest = shopOrderList?.some(
          (order) =>
            order.paymentDetail.status === PaymentState.PAID ||
            order.paymentDetail.status === PaymentState.CONFIRMED,
        );

        // If showPaidGuests is true, show all guests
        // If showPaidGuests is false, only show guests who haven't paid
        return showPaidGuests || !isPaidGuest;
      })
      .map((guest) => {
        return <GuestCard key={guest._id} guest={guest} />;
      });
  }, [guestUserList, showPaidGuests]);

  return (
    <div className="mx-auto max-w-6xl px-4 pb-8 sm:px-6 lg:px-8">
      <div className="mt-8 space-y-4">
        <div className="mb-4 flex items-center justify-end">
          <span className="mr-2 text-sm font-medium text-gray-700">
            {showPaidGuests ? '모든 고객 보기' : '구매유도 필요 고객만 보기'}
          </span>
          <Switch
            checked={showPaidGuests}
            onChange={setShowPaidGuests}
            className={`${
              showPaidGuests ? 'bg-blue-600' : 'bg-gray-200'
            } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
          >
            <span className="sr-only">구매완료 고객 표시</span>
            <span
              className={`${
                showPaidGuests ? 'translate-x-6' : 'translate-x-1'
              } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
            />
          </Switch>
        </div>
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <div className="grid w-full grid-cols-4 gap-2">{guestUserListRender}</div>
        </ErrorBoundary>
      </div>
    </div>
  );
};
